'use client';

import { Button } from '@/components/ui/button/button';
import { Image } from '@/components/ui/image/image';
import { cn } from '@/lib/utils';
import {
  buildFormContentFromLinkFields,
  toFormModalPayload,
  type LinkFieldsInput,
} from '@/lib/utils/form-data';
import { openFormModal } from '@/lib/utils/modal-event';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import type { CtaType, FooterLinkType, NestedMenuLink } from '../types';
import styles from './mobileNav.module.scss';

export type MobileNavProps = {
  menuLinks: NestedMenuLink[];
  ctas?: CtaType[];
  isOpen: boolean;
  onClose: () => void;
  footerLinks?: FooterLinkType[];
  setShowBackButton: (show: boolean) => void;
  setNavigateBack: (fn: (() => void) | null) => void;
};

type NavStackItem = {
  parent: NestedMenuLink | null;
  children: NestedMenuLink[];
};

const hasChildren = (link: NestedMenuLink): boolean => {
  return !!(link.children && link.children.length > 0);
};

const shouldUseLink = (link: NestedMenuLink): boolean => {
  return !!(link.href && link.href !== '' && !hasChildren(link));
};

// Accordion item component for top-level links with children
const AccordionItem = ({
  link,
  renderChildItem,
  openAccordion,
  toggleAccordion,
}: {
  link: NestedMenuLink;
  renderChildItem: (child: NestedMenuLink) => React.ReactElement | null;
  openAccordion: string | null;
  toggleAccordion: (id: string) => void;
}) => {
  const isOpen = openAccordion === link.id;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      resizeObserver.observe(contentRef.current);
      setContentHeight(contentRef.current.scrollHeight);

      return () => resizeObserver.disconnect();
    }
  }, [link.children]);

  return (
    <div className={styles.mobileMenu_section}>
      <button
        type="button"
        className={cn(styles.mobileMenu_sectionLink, styles.mobileMenu_accordionTrigger, {
          [styles.mobileMenu_accordionTrigger_open]: isOpen,
        })}
        onClick={() => toggleAccordion(link.id)}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${link.id}`}
      >
        <span>{link.label}</span>
        <ChevronDown
          size={20}
          className={cn(styles.mobileMenu_accordionIcon, {
            [styles.mobileMenu_accordionIcon_open]: isOpen,
          })}
        />
      </button>
      <div
        id={`accordion-content-${link.id}`}
        className={cn(styles.mobileMenu_accordionContent, {
          [styles.mobileMenu_accordionContent_open]: isOpen,
        })}
        role="region"
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
        }}
      >
        <div
          ref={contentRef}
          className={cn(styles.mobileMenu_accordionContentInner, {
            [styles.mobileMenu_accordionContentInner_open]: isOpen,
          })}
        >
          <div className={styles.mobileMenu_childrenContainer}>
            {link.children?.map((child) => renderChildItem(child))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MobileNav = ({
  menuLinks,
  ctas,
  isOpen,
  onClose,
  footerLinks,
  setShowBackButton,
  setNavigateBack,
}: MobileNavProps) => {
  const [navStack, setNavStack] = useState<NavStackItem[]>([{ parent: null, children: menuLinks }]);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  // Reset navigation state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setNavStack([{ parent: null, children: menuLinks }]);
      setOpenAccordion(null);
    }
  }, [isOpen, menuLinks]);

  const currentNav = navStack[navStack.length - 1];
  const isTopLevel = navStack.length === 1;

  const toggleAccordion = (id: string) => {
    setOpenAccordion((prev) => (prev === id ? null : id));
  };

  const navigateToChildren = (parent: NestedMenuLink) => {
    if (parent.children && parent.children.length > 0) {
      // Close any open accordions when navigating
      setOpenAccordion(null);
      // Use functional update to ensure we have the latest state
      setNavStack((prevStack) => [
        ...prevStack,
        { parent, children: parent.children as NestedMenuLink[] },
      ]);
    }
  };

  const navigateBack = useCallback(() => {
    setNavStack((prevStack) => {
      if (prevStack.length > 1) {
        return prevStack.slice(0, -1);
      }
      return prevStack;
    });
  }, []);

  const handleLinkClick = () => {
    onClose();
  };

  // Render top-level links with accordion behavior
  const renderTopLevelLink = (link: NestedMenuLink) => {
    const hasChildItems = hasChildren(link);
    const useLink = shouldUseLink(link);

    if (useLink) {
      return (
        <div key={link.id} className={styles.mobileMenu_section}>
          <Link
            className={styles.mobileMenu_sectionLink}
            href={link.href}
            target={link.newWindow ? '_blank' : undefined}
            rel={link.newWindow ? 'noopener noreferrer' : undefined}
            onClick={handleLinkClick}
          >
            <span>{link.label}</span>
          </Link>
        </div>
      );
    }

    if (hasChildItems) {
      return (
        <AccordionItem
          key={link.id}
          link={link}
          renderChildItem={renderChildItem}
          openAccordion={openAccordion}
          toggleAccordion={toggleAccordion}
        />
      );
    }

    return null;
  };

  // Render children items (with gray background)
  const renderChildItem = (child: NestedMenuLink) => {
    const hasGrandchildItems = hasChildren(child);
    const useLink = shouldUseLink(child);

    const content = (
      <>
        {child.icon?.src && (
          <div className={styles.mobileMenu_childIcon}>
            <Image src={child.icon.src} alt={child.icon.alt ?? 'icon'} width={18} height={18} />
          </div>
        )}
        <div className={styles.mobileMenu_childContent}>
          <span className={styles.mobileMenu_childLabel}>
            {child.label}
            {hasGrandchildItems && (
              <ArrowRight className={styles.mobileMenu_childArrow} width={14} height={20} />
            )}
          </span>
          {child.subtitle && (
            <span className={styles.mobileMenu_childSubtitle}>{child.subtitle}</span>
          )}
        </div>
      </>
    );

    if (useLink) {
      return (
        <div key={child.id} className={styles.mobileMenu_childItem}>
          <Link
            className={styles.mobileMenu_childLink}
            href={child.href}
            target={child.newWindow ? '_blank' : undefined}
            rel={child.newWindow ? 'noopener noreferrer' : undefined}
            onClick={handleLinkClick}
          >
            {content}
          </Link>
        </div>
      );
    }

    if (hasGrandchildItems) {
      return (
        <div key={child.id} className={styles.mobileMenu_childItem}>
          <div
            className={styles.mobileMenu_childLink}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToChildren(child);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                navigateToChildren(child);
              }
            }}
          >
            {content}
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (isTopLevel) {
      setShowBackButton(false);
      setNavigateBack(null);
    } else {
      setShowBackButton(true);
      setNavigateBack(() => navigateBack);
    }
  }, [isTopLevel, setShowBackButton, setNavigateBack, navigateBack]);

  return (
    <>
      <div
        className={cn(styles.mobileMenu_overlay, {
          [styles['mobileMenu_overlay--open']]: isOpen,
        })}
        onClick={onClose}
        role="presentation"
        data-testid="mobile-menu-overlay"
      />

      <nav
        className={cn(styles.mobileMenu, {
          [styles['mobileMenu--open']]: isOpen,
        })}
      >
        <div className={styles.mobileMenu_content}>
          {/* Navigation Links */}
          <div className={styles.mobileMenu_sections}>
            {/* Back Button (when not at top level) */}
            {/* {!isTopLevel && currentNav.parent && (
              <div className={styles.mobileMenu_backButton}>
                <button
                  type="button"
                  onClick={navigateBack}
                  className={styles.mobileMenu_backButton_content}
                  aria-label="Go back"
                >
                  <ArrowLeft width={18} height={23} />
                  <span>Back</span>
                </button>
              </div>
            )} */}

            {/* Parent Title (when showing grandchildren) */}
            {!isTopLevel && currentNav.parent && (
              <div className={styles.mobileMenu_parentTitle}>
                <h2 className={styles.mobileMenu_parentTitle_text}>{currentNav.parent.label}</h2>
              </div>
            )}

            {/* Top Level Links */}
            {isTopLevel && currentNav.children.map((link) => renderTopLevelLink(link))}

            {/* Children Items (with gray background) */}
            {!isTopLevel && (
              <div className={styles.mobileMenu_childrenContainer}>
                {currentNav.children.map((child) => renderChildItem(child))}
              </div>
            )}
          </div>

          {/* CTA and Footer Links Combined */}
          {((ctas && ctas.length > 0) || (footerLinks && footerLinks.length > 0)) && (
            <div className={styles.mobileMenu_ctaFooterWrapper}>
              {ctas && ctas.length > 0 && (
                <div className={styles.mobileMenu_ctas}>
                  {/* Reverse the array to show the last CTA first */}
                  {[...(ctas || [])].reverse().map((cta, index) => {
                    if (!cta) return null;
                    const ctaFields = cta.fields as LinkFieldsInput & {
                      key?: string;
                      label?: string;
                      url?: string;
                      newWindow?: boolean;
                      buttonStyle?: 'White' | 'Pink';
                    };
                    const ctaId = ctaFields?.key;
                    const formContent = buildFormContentFromLinkFields(ctaFields);
                    const formModalPayload = toFormModalPayload(formContent, { ctaId });

                    const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
                      if (ctaFields?.destination !== 'Form') {
                        handleLinkClick();
                        return;
                      }
                      if (!formModalPayload) {
                        console.warn(
                          `CTA "${ctaFields?.label ?? 'Unknown'}" has destination 'Form' but missing formId/formSlugId`,
                        );
                        handleLinkClick();
                        return;
                      }
                      event.preventDefault();
                      openFormModal(formModalPayload);
                      handleLinkClick();
                    };
                    if (!cta.fields.url && ctaFields?.destination !== 'Form') return null;
                    const ctaButton = (
                      <Button
                        withArrow={!(cta.fields.buttonStyle === 'White')}
                        variant={cta.fields.buttonStyle === 'White' ? 'secondary' : 'primary'}
                        className="w-full"
                      >
                        {cta.fields.label}
                      </Button>
                    );
                    return (
                      <div key={cta.sys?.id || `cta-${index}`} className={styles.mobileMenu_cta}>
                        {cta.fields.url ? (
                          <Link
                            href={cta.fields.url as Url}
                            target={cta.fields.newWindow ? '_blank' : undefined}
                            rel={cta.fields.newWindow ? 'noopener noreferrer' : undefined}
                            onClick={handleCtaClick}
                          >
                            {ctaButton}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (formModalPayload) openFormModal(formModalPayload);
                              handleLinkClick();
                            }}
                          >
                            {ctaButton}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* TODO: Add footer links back in if required */}
              {/* {footerLinks && footerLinks.length > 0 && (
                <div className={styles.mobileMenu_footerLinks}>
                  {footerLinks.map((link, index) => {
                    if (!link?.fields?.url || !link?.fields?.label) {
                      return null;
                    }
                    return (
                      <Link
                        href={link.fields.url}
                        key={`mobile-footer-link-${index}`}
                        className={styles.mobileMenu_footerLink}
                        target={link.fields.newWindow ? '_blank' : undefined}
                        rel={link.fields.newWindow ? 'noopener noreferrer' : undefined}
                        onClick={handleLinkClick}
                        aria-label={`Navigate to ${link.fields.label}`}
                      >
                        {link.fields.label}
                      </Link>
                    );
                  })}
                </div>
              )} */}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
